/**
 * Frame count helpers shared across capture pipeline modules.
 */

import { CANONICAL_FAILED_AT } from '../failureMetadata';
import { SFM_PERCENT_END } from './pipelinePercentConstants';
import { buildSparseSfmFailureDetail, parseSfmSparseCounts, SPARSE_SFM_FAILURE_CODE } from '../../constants/captureSchema';

/** Minimum registered cameras for sparse failure classification — only ``< 3`` is terminal. */
export const MIN_REGISTERED_CAMERAS = 3;

/** Completed captures with registered counts in [3, 10] show a viewer quality warning. */
export const LOW_MATCH_WARNING_MAX = 10;

/** Viewer hint copy for the 3–10 registered-images band. */
export const LOW_MATCH_QUALITY_WARNING_COPY =
  'Low quality due to low image matches from video.';

/** Coerce non-negative integer frame counts from Firestore / callbacks. */
export function coerceFrameInt(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

/** Read canonical frame fields with legacy `processing.frames` fallback for extracted count. */
export function readCaptureFrameFields(proc = {}) {
  const legacyFrames = coerceFrameInt(proc.frames);
  const framesExtracted = coerceFrameInt(proc.framesExtracted) ?? legacyFrames;
  const framesRegistered = coerceFrameInt(proc.framesRegistered);
  const framesMatchPool = coerceFrameInt(proc.framesMatchPool);
  return { framesExtracted, framesRegistered, framesMatchPool };
}

/** True when registered count indicates terminal sparse SfM failure. */
export function isSfmTooSparseFromFrameCounts(framesExtracted, framesRegistered) {
  const registered = coerceFrameInt(framesRegistered);
  if (registered == null) return false;
  return registered < MIN_REGISTERED_CAMERAS;
}

/** True when metadata indicates a sparse / low-registration SfM failure (including 360 ratio gate). */
export function isSparseSfmRegistrationFailure({
  failureCode = null,
  failureDetail = null,
  framesExtracted = null,
  framesRegistered = null,
} = {}) {
  const code = String(failureCode || '').trim().toUpperCase();
  if (code === SPARSE_SFM_FAILURE_CODE) return true;
  if (isSfmTooSparseFromFrameCounts(framesExtracted, framesRegistered)) return true;
  const detail = String(failureDetail || '').toLowerCase();
  if (detail.includes('too sparse')) return true;
  if (detail.includes('coverage gate failed')) return true;
  if (detail.includes('registration ratio') && detail.includes('below minimum')) return true;
  const parsed = parseSfmSparseCounts(failureDetail);
  if (parsed && parsed.matched < MIN_REGISTERED_CAMERAS) {
    return true;
  }
  return false;
}

/**
 * Derive canonical sparse SfM failure metadata from structured counts and/or failure code.
 * Returns null when evidence does not indicate a sparse SfM failure.
 */
export function deriveSfmSparseFailureMetadata({
  framesExtracted = null,
  framesRegistered = null,
  framesMatchPool = null,
  failureCode = null,
  failureDetail = null,
} = {}) {
  let extracted = coerceFrameInt(framesExtracted);
  let registered = coerceFrameInt(framesRegistered);
  let matchPool = coerceFrameInt(framesMatchPool);
  const parsed = parseSfmSparseCounts(failureDetail);
  if (parsed) {
    if (registered == null) registered = parsed.matched;
    if (matchPool == null) matchPool = parsed.total;
    if (extracted == null) extracted = parsed.total;
  }
  if (registered == null) return null;

  const sparse = isSparseSfmRegistrationFailure({
    failureCode,
    failureDetail,
    framesExtracted: extracted,
    framesRegistered: registered,
  });
  if (!sparse) return null;

  const pool = matchPool ?? extracted ?? parsed?.total ?? 0;
  return {
    failedAt: CANONICAL_FAILED_AT.SFM,
    failedStageIndex: 3,
    failedPercent: SFM_PERCENT_END,
    failureDetail: buildSparseSfmFailureDetail(registered, pool),
    failureCode: SPARSE_SFM_FAILURE_CODE,
  };
}

/** True when a completed capture should show the low-match quality warning (3–10 registered). */
export function isLowMatchQualityWarning(framesRegistered) {
  const registered = coerceFrameInt(framesRegistered);
  if (registered == null) return false;
  return registered >= MIN_REGISTERED_CAMERAS && registered <= LOW_MATCH_WARNING_MAX;
}

/** Classify registration outcome for analytics and logs. */
export function registrationQualityBucket(framesRegistered) {
  const registered = coerceFrameInt(framesRegistered);
  if (registered == null || registered < MIN_REGISTERED_CAMERAS) return 'lt3_fail';
  if (registered <= LOW_MATCH_WARNING_MAX) return '3to10_warn_success';
  return 'gt10_success';
}

/**
 * Derive canonical SfM-too-sparse failure metadata from structured frame counts.
 * Returns null when counts do not indicate a sparse SfM failure.
 */
export function deriveFailedStageFromFrameCounts(framesExtracted, framesRegistered, framesMatchPool = null) {
  if (!isSfmTooSparseFromFrameCounts(framesExtracted, framesRegistered)) return null;
  const registered = coerceFrameInt(framesRegistered);
  const pool = coerceFrameInt(framesMatchPool) ?? coerceFrameInt(framesExtracted) ?? 0;
  return {
    failedAt: CANONICAL_FAILED_AT.SFM,
    failedStageIndex: 3,
    failedPercent: SFM_PERCENT_END,
    failureDetail: buildSparseSfmFailureDetail(registered, pool),
  };
}
